<template>
  <Panel
    v-bind="panelForwardProps"
    :open="props.open"
    :items="groupedItems"
    :search="search"
    @update-search="handleUpdateSearch"
    @update-status="emit('update-status', $event)"
    @update-selected="handleUpdateSelected"
    @select-item="handleSelectItem"
    @search-enter="handleSearchEnter"
    @exit-start="emit('exit-start')"
  >
    <template #no-data>
      <slot name="no-data" />
    </template>
  </Panel>
</template>

<script setup>
/**
 * Navigation container selected by `navigation="flat"`. Renders one Panel
 * with optional search. No frame stack, no back button, no drill-in. Items
 * with `hasChildren` are stripped before reaching Panel (see `asFlatRow`).
 * The host wraps this container in NavigationContainerShell.
 */
import { computed, toRef } from "vue";
import { forwardablePanelProps, panelProps } from "#ce/components/SpQuantumSelect/props.js";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { booleanAttribute, toBoolean } from "#ce/utils/props";
import { useContainerSearch } from "#ce/components/SpQuantumSelect/composables/useContainerSearch.js";
import { useQuantumSelectItems } from "#ce/components/SpQuantumSelect/composables/useQuantumSelectItems";
import Panel from "./QuantumSelectPanel.ce.vue";

defineOptions({ inheritAttrs: false });

const emit = defineEmits([
  "update-status",
  "update-selected",
  "update-search",
  "select-item",
  "search-enter",
  "exit-start",
]);

const props = defineProps({
  ...panelProps,

  // Disables client-side filtering.
  noFilter: booleanAttribute(),
});

const noFilter = computed(() => toBoolean(props.noFilter));

const { getItemTitle } = useSpList(props);

const { search, applySearchInput } = useContainerSearch(props, emit);

function handleSearchEnter(event) {
  emit("search-enter", event);
}

function handleUpdateSearch(value) {
  applySearchInput(value);
}

function handleUpdateSelected(value) {
  emit("update-selected", value);
}

function handleSelectItem(item) {
  emit("select-item", item);
}

// Strip hasChildren so Panel does not render rows as non-selectable drill targets.
function asFlatRow(item) {
  if (!item?.hasChildren) {
    return item;
  }
  const copy = { ...item };
  delete copy.hasChildren;
  return copy;
}

const { groupedItems } = useQuantumSelectItems({
  items: computed(() => (props.items ?? []).map(asFlatRow)),
  search,
  noFilter,
  getItemTitle,
  maxResults: toRef(props, "maxResults"),
  fuzzySearch: computed(() => toBoolean(props.fuzzySearch)),
  highlightFirstResult: computed(() => toBoolean(props.highlightFirstResult)),
  highlightIdleFirstResult: computed(() => toBoolean(props.highlightIdleFirstResult)),
  selectableBranches: computed(() => toBoolean(props.selectableBranches)),
  sectionOrder: toRef(props, "sectionOrder"),
  noSubstringFallback: computed(() => toBoolean(props.noSubstringFallback)),
});

const panelForwardProps = computed(() => forwardablePanelProps(props));
</script>
